<template>
  <div
    class="accordion"
    role="presentation"
    :class="{ isExtended }"
    :data-testid="testId != undefined ? `${testId}-accordion` : null"
    :style="{ height: totalHeight }"
  >
    <button class="header" @click="toggle" ref="header" :aria-expanded="isExtended"
            :aria-controls="'accordion-section-' + accordionId" :id="'accordion-header-' + accordionId">
      <span aria-hidden="true" class="header-icon" v-if="icon"></span>
      <span aria-hidden="true" class="header-control"></span>
      <span class="header-title">{{ title }}</span>
    </button>
    <section :data-testid="testId != undefined ? testId : null" class="content" ref="content" :inert="!isExtended"
             :id="'accordion-section-' + accordionId" :aria-labelledby="'accordion-header-' + accordionId">
      <slot><!-- Default content --></slot>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
  props: {
    title: {
      type: String,
      required: true,
    },
    testId:{
      type: String,
      required: false
    },
    icon: {
      type: String,
    },
    preopened: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isExtended: false,
      headerHeight: 64, // initial height
      contentHeight: 0,
      accordionId: "",
    };
  },
  created() {
    this.accordionId =
      "id" + window.crypto.getRandomValues(new Uint32Array(1)).toString();
    this.isExtended = this.preopened;

    if (!this.$slots.default)
      console.warn(
        "Accordion without content. Provide a default slot as expandable content."
      );
  },
  mounted() {
    this.calculateHeights();
    window.addEventListener("resize", this.calculateHeights);
  },
  unmounted() {
    window.removeEventListener("resize", this.calculateHeights);
  },
  methods: {
    toggle() {
      this.isExtended = !this.isExtended;
      this.$emit("extended", this.isExtended);

      // Used for AccordionGroup, and done with $parent b/c of the anonymous use via slots
      // Vue 3: $parent.$options is different, check parent component name
      const isPartOfGroup =
        this.$parent?.$.type?.name === "RhAccordionGroup" ||
        this.$parent?.$.type?.__name === "RhAccordionGroup";
      if (isPartOfGroup) (this.$parent as any).closeOther(this);
    },
    calculateHeights() {
      this.headerHeight = (this.$refs.header as HTMLElement).clientHeight + 4;
      this.contentHeight = (this.$refs.content as HTMLElement).clientHeight;
    },
  },
  computed: {
    totalHeight(): string {
      return (
        (this.isExtended
          ? this.headerHeight + this.contentHeight
          : this.headerHeight) + "px"
      );
    },
  },
});
</script>

<style lang="scss" scoped>
$color-accordion-collapsed: #e1e9f3;
$color-accordion-collapsed-control: #404146;
$color-accordion-extended: $color-primary-blue;
$color-accordion-extended-control: #fff;

.accordion {
  height: 64px;
  transition: height 0.4s $animation-key;
  overflow: hidden;

  &.isExtended {
    .header {
      background-color: $color-accordion-extended;

      &-control {
        &::before,
        &::after {
          background-color: $color-accordion-extended-control;
        }
        &::before {
          transform: none;
        }
        &::after {
          transform: rotateZ(0deg);
        }
      }
      &-title {
        color: #fff;
      }
    }
  }
}

.header {
  display: flex;
  padding: 10px 20px 10px 0;
  min-height: 64px;
  transition: background-color 0.4s ease;
  cursor: pointer;
  user-select: none;
  width: 100%;
  background-color: $color-accordion-collapsed;
  -webkit-clip-path: polygon(
    0 0,
    0 100%,
    calc(100% - 25px) 100%,
    100% calc(100% - 25px),
    100% 0
  );
  clip-path: polygon(
    0 0,
    0 100%,
    calc(100% - 25px) 100%,
    100% calc(100% - 25px),
    100% 0
  );

  &-icon {
    background-color: $color-primary-orange;
    width: 64px;
    min-width: 64px;
    padding-top: 32px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    color: #fff;
    fill: #fff;
  }

  &-control {
    width: 64px;
    height: 44px; // -20px to compensate padding of .header
    min-width: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 2em;
    &::before,
    &::after {
      content: "";
      width: 18px;
      height: 3px;
      background-color: $color-accordion-collapsed-control;
      position: absolute;
      transition: transform 0.4s ease;
    }
    &::before {
      transform: rotateZ(-180deg);
    }
    &::after {
      transform: rotateZ(-90deg);
    }
  }
  &-title {
    display: flex;
    align-items: center;
    color: $color-accordion-collapsed-control;
  }

  &:focus {
    outline: none;
    border: 2px solid $color-primary-blue;
  }
}

.content {
  color: $font-color-gray;
  padding: 24px 24px 38px;
  max-width: 920px;
}

.header-title{
  font-size: 16px;
}
</style>
